// The console's copy of the company (WEB_PLAN.md §3.2): every row the caller can read, exactly as the server sent
// it, plus the outbox of edits not yet confirmed. Screens are pure functions of this; pull.ts and push.ts are the
// only writers.
//
// - In memory: per collection, a Map from lower-case id to CachedRow (the raw server payload, never re-encoded).
// - Outbox: per collection, a Map from lower-case id to OutboxEntry (§3.6). Rows and entries are immutable values:
//   every change stores a new object, which is what lets views and React selectors keep identities stable.
// - Live view = server rows overlaid with pending saves (optimistic) minus pending deletes, cached per collection
//   change version, so `useRows('records')` re-renders only when records (or their outbox entries) changed.
// - Persistence: IndexedDB `gt:<authUserId>:<companyId>` (stores rows / outbox / meta) written after each applied
//   page and after edits. Only a start-up speed-up (and edits surviving a reload): a missing database or a different
//   schema version starts from cursor 0.

import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { SYNC_COLLECTIONS, type Collection } from '@/contracts/collections';
import { idKey } from '@/contracts/uuid';
import type { JsonObject } from '@/contracts/wire';
import { deepEqual } from './merge';

/** One server row as the replica keeps it. Treat as read-only (payloads are shared with every view). */
export interface CachedRow {
  /** Upper-case id (Swift `uuidString` form; the server envelope sends lower-case). */
  id: string;
  /** Raw payload as received (local-only keys and all); for an optimistic row, the pending local payload. */
  payload: JsonObject;
  /** Server version (0 for an item that has never been saved). */
  version: number;
  serverSeq: number;
  /** Envelope `modified_at` (ms precision); the pending edit time for an optimistic row. */
  modifiedAt: string;
  /** Envelope `modified_by`: a phone's device id or `web-<uuid>`. */
  modifiedBy: string;
  /** `<company>/<collection>/<fileName>` for attachments/documents that have a file. */
  filePath?: string;
}

/** A local edit waiting for the server (§3.6). */
export interface OutboxEntry {
  collection: Collection;
  /** Id as the payload writes it (upper-case). */
  id: string;
  kind: 'save' | 'delete';
  /** Canonical server payload the edit started from (the merge base); null for a new item. */
  basePayload: JsonObject | null;
  /** That row's version, sent as `base_version`; null for a new item. */
  baseVersion: number | null;
  /** The payload to push (patches applied, local-only keys stripped, customValues canonical); null for deletes. */
  localPayload: JsonObject | null;
  /** Edit time (ms precision, server-clock corrected). */
  modifiedAt: string;
  filePath?: string;
  /** Times this entry was sent (informational). */
  attempts: number;
  /** 'failed' = the server refused it as invalid; kept (shown "Not saved") until edited again, retried or discarded. */
  state: 'pending' | 'failed';
  /** Last server message for this entry. */
  error?: string;
  /** Replica-wide edit sequence number of the latest edit (detects edits made while a push was in flight). */
  rev: number;
}

export interface ReplicaMeta {
  /** Change-feed cursor (`server_seq` of the last row seen). 0 = never pulled. */
  cursor: number;
  /** Visibility epoch of the last completed pull; null before the first one. */
  epoch: number | null;
  /** A pull has completed at least once (screens can tell "empty" from "loading"). */
  initialFetchDone: boolean;
  /** ISO time of the last successful pull. */
  lastSyncedAt: string | null;
  /** Source of `OutboxEntry.rev`. */
  editSeq: number;
}

/** Per-row sync state for list chips ("Saving…", "Not saved"). */
export interface RowState {
  /** A pending edit (queued or in flight). */
  saving: boolean;
  /** The server refused the edit ('invalid'); offer Discard. */
  failed: boolean;
  /** A pending delete. */
  deleting: boolean;
  error?: string;
}

/** Bump when the stored shapes change: older databases are dropped and the replica starts from cursor 0. */
export const REPLICA_SCHEMA_VERSION = 1;

/** Failed persist transactions in a row after which the database is given up for this session. */
export const MAX_PERSIST_FAILURES = 3;

const META_KEY = 'state';
const KNOWN_DBS_KEY = 'gt:dbs';
const DEVICE_ID_KEY = 'gt:deviceId';

interface StoredMeta extends ReplicaMeta {
  schema: number;
}

interface ReplicaDB extends DBSchema {
  rows: { key: [string, string]; value: CachedRow };
  outbox: { key: [string, string]; value: OutboxEntry };
  meta: { key: string; value: StoredMeta };
}

const INITIAL_META: ReplicaMeta = { cursor: 0, epoch: null, initialFetchDone: false, lastSyncedAt: null, editSeq: 0 };

/** IndexedDB database name for one account in one company (§3.2). */
export function replicaDbName(authUserId: string, companyId: string): string {
  return `gt:${authUserId.toLowerCase()}:${companyId.toLowerCase()}`;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

let memoryDeviceId: string | null = null;

/** This browser's stable `modified_by` (`web-<uuid>`, kept in localStorage; one per browser, not per tab). */
export function browserDeviceId(): string {
  const ls = storage();
  try {
    const existing = ls?.getItem(DEVICE_ID_KEY);
    if (existing && existing.startsWith('web-') && existing.length <= 256) return existing;
  } catch {
    // fall through
  }
  memoryDeviceId ??= `web-${crypto.randomUUID()}`;
  try {
    ls?.setItem(DEVICE_ID_KEY, memoryDeviceId);
  } catch {
    // Private mode: one id for this page's lifetime.
  }
  return memoryDeviceId;
}

/** Names of replica databases this browser opened (fallback for browsers without `indexedDB.databases()`). */
export function knownReplicaDbNames(): string[] {
  try {
    const raw = storage()?.getItem(KNOWN_DBS_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((n): n is string => typeof n === 'string' && n.startsWith('gt:')) : [];
  } catch {
    return [];
  }
}

function rememberDbName(name: string): void {
  const names = new Set(knownReplicaDbNames());
  if (names.has(name)) return;
  names.add(name);
  try {
    storage()?.setItem(KNOWN_DBS_KEY, JSON.stringify([...names]));
  } catch {
    // Not critical.
  }
}

export function forgetKnownReplicaDbNames(): void {
  try {
    storage()?.removeItem(KNOWN_DBS_KEY);
  } catch {
    // Not critical.
  }
}

interface View {
  version: number;
  list: CachedRow[];
  byId: Map<string, CachedRow>;
  states: Map<string, RowState> | null;
}

type Listener = () => void;

const EMPTY_STATES: ReadonlyMap<string, RowState> = new Map();

export class Replica {
  readonly deviceId: string;

  private rows = new Map<Collection, Map<string, CachedRow>>();
  private outbox = new Map<Collection, Map<string, OutboxEntry>>();
  private metaValue: ReplicaMeta = { ...INITIAL_META };

  private versions = new Map<Collection, number>();
  private views = new Map<Collection, View>();
  private overlays = new WeakMap<OutboxEntry, { row: CachedRow | undefined; live: CachedRow }>();
  private listeners = new Map<Collection | '*', Set<Listener>>();
  private pendingNotify = new Set<Collection>();
  private notifyScheduled = false;
  private outboxCount = 0;
  private failedCountValue = 0;

  private db: IDBPDatabase<ReplicaDB> | null = null;
  private dbName: string | null = null;
  private dirtyRows = new Map<string, { key: [string, string]; row: CachedRow | null }>();
  private dirtyOutbox = new Map<string, { key: [string, string]; entry: OutboxEntry | null }>();
  private metaDirty = false;
  private persistChain: Promise<void> = Promise.resolve();
  /** Consecutive failed `persist` transactions. */
  private persistFailures = 0;

  constructor(deviceId: string = browserDeviceId()) {
    this.deviceId = deviceId;
    for (const c of SYNC_COLLECTIONS) {
      this.rows.set(c, new Map());
      this.outbox.set(c, new Map());
      this.versions.set(c, 0);
    }
  }

  // MARK: - Meta

  get meta(): Readonly<ReplicaMeta> {
    return this.metaValue;
  }

  updateMeta(changes: Partial<ReplicaMeta>): void {
    this.metaValue = { ...this.metaValue, ...changes };
    this.metaDirty = true;
  }

  /** Next edit sequence number (stored with the meta). */
  nextRev(): number {
    const rev = this.metaValue.editSeq + 1;
    this.updateMeta({ editSeq: rev });
    return rev;
  }

  // MARK: - Server rows

  getRow(collection: Collection, id: string): CachedRow | undefined {
    return this.rows.get(collection)!.get(idKey(id));
  }

  /** Every server row of a collection (including rows hidden by a pending delete). */
  serverRows(collection: Collection): IterableIterator<CachedRow> {
    return this.rows.get(collection)!.values();
  }

  rowCount(collection?: Collection): number {
    if (collection) return this.rows.get(collection)!.size;
    let n = 0;
    for (const m of this.rows.values()) n += m.size;
    return n;
  }

  /**
   * Stores a server row. Idempotent: the same version with the same payload (a re-emit, M1:479-484) only refreshes
   * `serverSeq` without notifying anyone. Returns true when something visible changed.
   */
  putRow(collection: Collection, row: CachedRow): boolean {
    const map = this.rows.get(collection)!;
    const k = idKey(row.id);
    const old = map.get(k);
    this.dirtyRows.set(`${collection}/${k}`, { key: [collection, k], row });
    const same =
      old !== undefined &&
      old.version === row.version &&
      old.modifiedAt === row.modifiedAt &&
      old.modifiedBy === row.modifiedBy &&
      old.filePath === row.filePath &&
      deepEqual(old.payload, row.payload);
    if (same) {
      // Nothing visible changed: keep the old object (stable identity for selectors); the stored copy gets the new
      // serverSeq, which nothing in memory reads.
      return false;
    }
    map.set(k, row);
    this.changed(collection);
    return true;
  }

  removeRow(collection: Collection, id: string): boolean {
    const k = idKey(id);
    if (!this.rows.get(collection)!.delete(k)) return false;
    this.dirtyRows.set(`${collection}/${k}`, { key: [collection, k], row: null });
    this.changed(collection);
    return true;
  }

  // MARK: - Outbox

  getEntry(collection: Collection, id: string): OutboxEntry | undefined {
    return this.outbox.get(collection)!.get(idKey(id));
  }

  /** Every outbox entry (any order). */
  entries(): OutboxEntry[] {
    const out: OutboxEntry[] = [];
    for (const m of this.outbox.values()) for (const e of m.values()) out.push(e);
    return out;
  }

  entriesOf(collection: Collection): IterableIterator<OutboxEntry> {
    return this.outbox.get(collection)!.values();
  }

  /** Stores an entry (a new object: entries are immutable values). */
  putEntry(entry: OutboxEntry): void {
    const map = this.outbox.get(entry.collection)!;
    const k = idKey(entry.id);
    const old = map.get(k);
    if (!old) this.outboxCount++;
    if (old?.state === 'failed') this.failedCountValue--;
    if (entry.state === 'failed') this.failedCountValue++;
    map.set(k, entry);
    this.dirtyOutbox.set(`${entry.collection}/${k}`, { key: [entry.collection, k], entry });
    this.changed(entry.collection);
  }

  removeEntry(collection: Collection, id: string): boolean {
    const map = this.outbox.get(collection)!;
    const k = idKey(id);
    const old = map.get(k);
    if (!old) return false;
    map.delete(k);
    this.outboxCount--;
    if (old.state === 'failed') this.failedCountValue--;
    this.dirtyOutbox.set(`${collection}/${k}`, { key: [collection, k], entry: null });
    this.changed(collection);
    return true;
  }

  /** Outbox entries (pending and failed): what sign-out would leave unsaved. */
  get outboxSize(): number {
    return this.outboxCount;
  }

  get failedCount(): number {
    return this.failedCountValue;
  }

  /** The payload screens see for an item: a pending save's payload, null under a pending delete, else the row's. */
  effectivePayload(collection: Collection, id: string): JsonObject | null {
    const entry = this.getEntry(collection, id);
    if (entry) return entry.kind === 'save' ? entry.localPayload : null;
    return this.getRow(collection, id)?.payload ?? null;
  }

  // MARK: - Live views (what hooks return)

  /** Change counter of a collection (rows or outbox). */
  version(collection: Collection): number {
    return this.versions.get(collection)!;
  }

  /** Live, non-deleted rows reflecting optimistic outbox payloads. Same array until the collection changes. */
  liveRows(collection: Collection): readonly CachedRow[] {
    return this.view(collection).list;
  }

  liveRow(collection: Collection, id: string | null | undefined): CachedRow | undefined {
    if (!id) return undefined;
    return this.view(collection).byId.get(idKey(id));
  }

  /** Sync state per lower-case id, for rows with an outbox entry. Same Map until the collection changes. */
  rowStates(collection: Collection): ReadonlyMap<string, RowState> {
    const view = this.view(collection);
    if (view.states) return view.states;
    const map = this.outbox.get(collection)!;
    if (map.size === 0) {
      view.states = EMPTY_STATES as Map<string, RowState>;
      return view.states;
    }
    const states = new Map<string, RowState>();
    for (const [k, e] of map) {
      const state: RowState = { saving: e.state === 'pending', failed: e.state === 'failed', deleting: e.kind === 'delete' };
      if (e.error !== undefined) state.error = e.error;
      states.set(k, state);
    }
    view.states = states;
    return states;
  }

  private view(collection: Collection): View {
    const version = this.versions.get(collection)!;
    const cached = this.views.get(collection);
    if (cached && cached.version === version) return cached;

    const rows = this.rows.get(collection)!;
    const entries = this.outbox.get(collection)!;
    const list: CachedRow[] = [];
    const byId = new Map<string, CachedRow>();
    for (const [k, row] of rows) {
      const entry = entries.get(k);
      let live: CachedRow | undefined = row;
      if (entry) live = entry.kind === 'delete' ? undefined : this.overlay(entry, row);
      if (!live) continue;
      list.push(live);
      byId.set(k, live);
    }
    for (const [k, entry] of entries) {
      if (rows.has(k) || entry.kind === 'delete' || !entry.localPayload) continue;
      const live = this.overlay(entry, undefined);
      list.push(live);
      byId.set(k, live);
    }
    const view: View = { version, list, byId, states: null };
    this.views.set(collection, view);
    return view;
  }

  private overlay(entry: OutboxEntry, row: CachedRow | undefined): CachedRow {
    const cached = this.overlays.get(entry);
    if (cached && cached.row === row) return cached.live;
    const live: CachedRow = {
      id: row?.id ?? entry.id,
      payload: entry.localPayload ?? row?.payload ?? {},
      version: row?.version ?? 0,
      serverSeq: row?.serverSeq ?? 0,
      modifiedAt: entry.modifiedAt,
      modifiedBy: this.deviceId,
    };
    const filePath = entry.filePath ?? row?.filePath;
    if (filePath !== undefined) live.filePath = filePath;
    this.overlays.set(entry, { row, live });
    return live;
  }

  // MARK: - Change notification

  /**
   * Listens for changes to one collection (or to any, with '*'). Listeners run once per microtask batch, after a
   * whole page or push result has been applied.
   */
  subscribe(collection: Collection | '*', listener: Listener): () => void {
    let set = this.listeners.get(collection);
    if (!set) {
      set = new Set();
      this.listeners.set(collection, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  private changed(collection: Collection): void {
    this.versions.set(collection, this.versions.get(collection)! + 1);
    this.pendingNotify.add(collection);
    if (!this.notifyScheduled) {
      this.notifyScheduled = true;
      queueMicrotask(() => this.flushNotify());
    }
  }

  /** Delivers pending notifications now (normally automatic at the end of the microtask). */
  flushNotify(): void {
    this.notifyScheduled = false;
    if (this.pendingNotify.size === 0) return;
    const changed = [...this.pendingNotify];
    this.pendingNotify.clear();
    const called = new Set<Listener>();
    const call = (l: Listener) => {
      if (called.has(l)) return;
      called.add(l);
      try {
        l();
      } catch (error) {
        console.error('replica listener failed', error);
      }
    };
    for (const c of changed) this.listeners.get(c)?.forEach(call);
    this.listeners.get('*')?.forEach(call);
  }

  private changedAll(): void {
    for (const c of SYNC_COLLECTIONS) this.changed(c);
  }

  // MARK: - Reset

  /** Forgets every row, entry and the cursor (memory only; persisted on the next `persist()`). */
  clear(): void {
    for (const c of SYNC_COLLECTIONS) {
      for (const k of this.rows.get(c)!.keys()) this.dirtyRows.set(`${c}/${k}`, { key: [c, k], row: null });
      for (const k of this.outbox.get(c)!.keys()) this.dirtyOutbox.set(`${c}/${k}`, { key: [c, k], entry: null });
      this.rows.set(c, new Map());
      this.outbox.set(c, new Map());
    }
    this.outboxCount = 0;
    this.failedCountValue = 0;
    this.metaValue = { ...INITIAL_META };
    this.metaDirty = true;
    this.changedAll();
  }

  // MARK: - Persistence

  /** True while an IndexedDB connection is open. */
  get isPersistent(): boolean {
    return this.db !== null;
  }

  /**
   * Opens (or creates) the database and replaces memory with what it holds. A missing database, another schema
   * version or an unreadable store means starting from cursor 0. Without IndexedDB (private mode, tests) the replica
   * runs in memory only and this returns false.
   */
  async open(dbName: string): Promise<boolean> {
    await this.close();
    if (this.dbName !== null && this.dbName !== dbName) this.clear(); // never show one company's rows under another
    this.dbName = dbName;
    if (typeof indexedDB === 'undefined') return false;
    let db: IDBPDatabase<ReplicaDB>;
    try {
      db = await this.openDatabase(dbName);
    } catch (error) {
      console.warn('Replica database unavailable; running in memory only.', error);
      return false;
    }
    rememberDbName(dbName);

    let meta: StoredMeta | undefined;
    let rowKeys: [string, string][] = [];
    let rowValues: CachedRow[] = [];
    let entryValues: OutboxEntry[] = [];
    try {
      const tx = db.transaction(['rows', 'outbox', 'meta'], 'readonly');
      [meta, rowKeys, rowValues, entryValues] = await Promise.all([
        tx.objectStore('meta').get(META_KEY),
        tx.objectStore('rows').getAllKeys(),
        tx.objectStore('rows').getAll(),
        tx.objectStore('outbox').getAll(),
        tx.done,
      ]);
    } catch (error) {
      console.warn('Replica database unreadable; starting over.', error);
      meta = undefined;
    }

    // Replace memory (the in-memory state belongs to whatever this replica held before).
    this.clear();
    this.dirtyRows.clear();
    this.dirtyOutbox.clear();
    this.metaDirty = false;
    this.persistFailures = 0;
    this.db = db;

    if (!meta || meta.schema !== REPLICA_SCHEMA_VERSION) {
      // Missing or from another schema: wipe the stores and start from cursor 0.
      await this.resetStores();
      return true;
    }
    for (let i = 0; i < rowKeys.length; i++) {
      const [c, k] = rowKeys[i];
      const map = this.rows.get(c as Collection);
      if (map && rowValues[i]) map.set(k, rowValues[i]);
    }
    for (const e of entryValues) {
      const map = this.outbox.get(e.collection);
      if (!map) continue;
      map.set(idKey(e.id), e);
      this.outboxCount++;
      if (e.state === 'failed') this.failedCountValue++;
    }
    this.metaValue = {
      cursor: meta.cursor,
      epoch: meta.epoch,
      initialFetchDone: meta.initialFetchDone,
      lastSyncedAt: meta.lastSyncedAt,
      editSeq: meta.editSeq,
    };
    this.changedAll();
    return true;
  }

  private async openDatabase(name: string): Promise<IDBPDatabase<ReplicaDB>> {
    const open = () =>
      openDB<ReplicaDB>(name, REPLICA_SCHEMA_VERSION, {
        upgrade(db) {
          // Any older layout is dropped: the replica is a cache and simply starts from cursor 0.
          for (const store of [...db.objectStoreNames]) db.deleteObjectStore(store);
          db.createObjectStore('rows');
          db.createObjectStore('outbox');
          db.createObjectStore('meta');
        },
        blocking: () => {
          // Another tab upgrades, or wipeAllLocalData deletes the database: let go of it.
          this.dropConnection();
        },
        terminated: () => {
          this.db = null;
        },
      });
    try {
      return await open();
    } catch (error) {
      // A newer schema on disk (a later console build opened it): drop it and start over.
      if (error instanceof DOMException && error.name === 'VersionError') {
        await deleteDB(name);
        return open();
      }
      throw error;
    }
  }

  private dropConnection(): void {
    const db = this.db;
    this.db = null;
    db?.close();
  }

  private async resetStores(): Promise<void> {
    const db = this.db;
    if (!db) return;
    try {
      const tx = db.transaction(['rows', 'outbox', 'meta'], 'readwrite');
      await Promise.all([
        tx.objectStore('rows').clear(),
        tx.objectStore('outbox').clear(),
        tx.objectStore('meta').put({ ...this.metaValue, schema: REPLICA_SCHEMA_VERSION }, META_KEY),
        tx.done,
      ]);
    } catch (error) {
      console.warn('Replica database reset failed; running in memory only.', error);
      this.dropConnection();
    }
  }

  /**
   * Writes every change since the last call in one transaction (rows, outbox, meta). Calls are serialised.
   *
   * The transaction is all or nothing, and a batch that fails goes back into the dirty set (changes made meanwhile
   * win), so the stored cursor is never ahead of the rows and outbox entries stored with it: dropping a failed batch
   * would let a later, successful write store a newer cursor without those rows, and a reload would then miss them
   * (and lose the edits in that batch). After MAX_PERSIST_FAILURES failures in a row (quota exhausted, say) the
   * database is deleted and the replica runs from memory: the next load starts from cursor 0 instead of from a copy
   * that can no longer be kept current.
   */
  persist(): Promise<void> {
    const run = async () => {
      const db = this.db;
      const rows = this.dirtyRows;
      const outbox = this.dirtyOutbox;
      const metaDirty = this.metaDirty;
      this.dirtyRows = new Map();
      this.dirtyOutbox = new Map();
      this.metaDirty = false;
      // Without a database (memory only, or let go of after `blocking`) the stored copy stays as it was.
      if (!db || (rows.size === 0 && outbox.size === 0 && !metaDirty)) return;
      let abort: (() => void) | null = null;
      const ops: Promise<unknown>[] = [];
      try {
        const tx = db.transaction(['rows', 'outbox', 'meta'], 'readwrite');
        abort = () => tx.abort();
        const done = tx.done;
        done.catch(() => undefined); // an abort below rejects it; the error is handled here, not as an unhandled rejection
        const rowStore = tx.objectStore('rows');
        const outboxStore = tx.objectStore('outbox');
        for (const { key, row } of rows.values()) ops.push(row ? rowStore.put(row, key) : rowStore.delete(key));
        for (const { key, entry } of outbox.values()) ops.push(entry ? outboxStore.put(entry, key) : outboxStore.delete(key));
        if (metaDirty) ops.push(tx.objectStore('meta').put({ ...this.metaValue, schema: REPLICA_SCHEMA_VERSION }, META_KEY));
        ops.push(done);
        await Promise.all(ops);
        this.persistFailures = 0;
      } catch (error) {
        try {
          abort?.(); // nothing of a half-queued batch may commit
        } catch {
          // Already committed or aborted.
        }
        void Promise.allSettled(ops); // requests queued before the failure reject on abort: nothing else awaits them
        for (const [k, v] of rows) if (!this.dirtyRows.has(k)) this.dirtyRows.set(k, v);
        for (const [k, v] of outbox) if (!this.dirtyOutbox.has(k)) this.dirtyOutbox.set(k, v);
        if (metaDirty) this.metaDirty = true;
        this.persistFailures++;
        console.warn('Replica persistence failed', error);
        if (this.persistFailures >= MAX_PERSIST_FAILURES && this.db === db) await this.abandonDatabase();
      }
    };
    this.persistChain = this.persistChain.then(run, run);
    return this.persistChain;
  }

  /** Stops persisting for this session and deletes the database (see `persist`). Memory stays as it is. */
  private async abandonDatabase(): Promise<void> {
    const name = this.dbName;
    console.warn('Replica database keeps failing; deleting it and continuing in memory only.');
    this.dropConnection();
    this.dirtyRows = new Map();
    this.dirtyOutbox = new Map();
    this.metaDirty = false;
    this.persistFailures = 0;
    if (name && typeof indexedDB !== 'undefined') await deleteDatabase(name);
  }

  /** Writes what is still dirty, then closes the database (memory is kept; `open` reloads). */
  async close(): Promise<void> {
    await this.persist().catch(() => undefined);
    this.dropConnection();
  }

  /** Closes and deletes this replica's database, and empties memory. */
  async destroy(): Promise<void> {
    const name = this.dbName;
    await this.close();
    this.clear();
    this.dirtyRows.clear();
    this.dirtyOutbox.clear();
    this.metaDirty = false;
    if (name && typeof indexedDB !== 'undefined') {
      await deleteDatabase(name);
    }
  }
}

/** Deletes one database, giving up after `timeoutMs` if another tab keeps it open (it closes on `versionchange`). */
export async function deleteDatabase(name: string, timeoutMs = 3000): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      deleteDB(name),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } catch (error) {
    console.warn(`Could not delete ${name}`, error);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
